//july17 FormContex.js:
// FormContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { doc, setDoc, serverTimestamp, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../firebase';

import { v4 as uuidv4 } from 'uuid'; // Import uuid to generate unique identifiers
import { checkUserPermission, getFormById } from '../utils/firestore'; // Adjust the path as needed to import the function
import { safeRemoveItem } from '../utils/storageUtils'; // Add safeRemoveItem

// Context creation
const FormContext = createContext();

export const FormProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate(); // Initialize navigate to update URL when needed
  const { formId: urlFormId } = useParams();

  // Initialize formId state (start with null; it will be set based on localStorage or URL)
  const [formId, setFormId] = useState(null);
  const [formIdReady, setFormIdReady] = useState(false); // NEW: Flag to track readiness
  const memoizedFormId = useMemo(() => formId, [formId]); // Memoize formId to avoid unnecessary re-renders

  // State for form title and description
  const [formTitle, setFormTitle] = useState(null);
  const [formDescription, setFormDescription] = useState(null);

  // Centralized state for form details
  const [formDetails, setFormDetails] = useState({});
  const [isPublished, setIsPublished] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false); // Manage form validity
  const [isFormModified, setIsFormModified] = useState(false); // Track modifications

  // Memoize title and description to avoid unnecessary re-renders
  const memoizedFormTitle = useMemo(() => formTitle, [formTitle]);
  const memoizedFormDescription = useMemo(() => formDescription, [formDescription]);

  const previousFormId = useRef(formId); // Track the previous form ID
  const [isFormBeingCreated, setIsFormBeingCreated] = useState(false); // Prevent duplicate creation

  // Helper function to construct storage keys
  const makeKey = useCallback((key) => `form_${formId}_${key}`, [formId]);

// ============================================================
// UPDATED: UseEffect to initialize formId from URL state or
// route param or localStorage. If no formId is found, we do
// NOT auto-generate a new one. We rely solely on the permanent
// ID from createNewForm() (triggered by the user).
// ============================================================
useEffect(() => {
  const initializeFormId = () => {
//    console.log('[FormContext: initializeFormId] initializeFormId triggered');

    // If the location state indicates new form creation
    // or if the current URL path includes '/form-builder/new/design',
    // then we clear any existing formId. This route is specifically
    // for "starting a brand-new form," so we skip auto-generation here.
    if (
      (location.state && location.state.createNewForm === true) &&
      !urlFormId // only clear formId if no real formId in the URL
    ) {

//      console.log('[FormContext: initializeFormId] Detected new form creation mode. Clearing existing formId.');
      localStorage.removeItem('formId');
      sessionStorage.removeItem('formId');
      setFormId(null);
      setFormIdReady(false);
      return;
    }

    let formIdToUse = null;

    // Priority 1: Check if formId is passed via location state
    if (location.state && location.state.formId) {
      formIdToUse = location.state.formId;
//      console.log(`[FormContext: initializeFormId] Retrieved formId ${formIdToUse} from location state.`);
    }
    // ✅ NEW: Priority 2 - Check if formId is available in the URL params
    else if (urlFormId) {
      formIdToUse = urlFormId; // Extracted from useParams()
//      console.log(`[FormContext: initializeFormId] Retrieved formId ${formIdToUse} from URL params.`);
    }
    // Priority 3: Check if formId exists in localStorage
    else {
      formIdToUse = localStorage.getItem('formId');
      if (formIdToUse) {
//        console.log(`[FormContext: initializeFormId] Retrieved formId ${formIdToUse} from localStorage.`);
      }
    }

    // Priority 4: If no formId found, DO NOT auto-generate a new one.
    // We only create a new ID when the user explicitly clicks "New Form."
    if (!formIdToUse) {
//      console.log('[FormContext: initializeFormId] No formId found in location state, URL params, or localStorage. Setting formId to null.');
      setFormId(null);
      setFormIdReady(false);
      return;
    }

    // Otherwise, set the existing formId we found
//    console.log(`[FormContext: initializeFormId] Setting formId to: ${formIdToUse}`);
    setFormId(formIdToUse);
    setFormIdReady(true);
  };

  initializeFormId();
}, [location.state, navigate, location.pathname, urlFormId]); 


  // ============================================================

  // Function to get data from sessionStorage or localStorage
  const getFromStorage = useCallback((key) => {
    const sessionData = sessionStorage.getItem(key);
    if (sessionData) {
      try {
        return JSON.parse(sessionData);
      } catch {
        return sessionData;
      }
    }
    const localData = localStorage.getItem(key);
    if (localData) {
      try {
        return JSON.parse(localData);
      } catch {
        return localData;
      }
    }
    return null;
  }, []);

  // Function to save data to sessionStorage and localStorage
  const saveToStorage = useCallback((key, value) => {
    const valueToStore = typeof value === 'string' ? value : JSON.stringify(value);
    try {
      sessionStorage.setItem(key, valueToStore);
    } catch (sessionError) {
      console.warn(`[FormContext] SessionStorage quota exceeded for ${key}, falling back to localStorage.`);
      try {
        localStorage.setItem(key, valueToStore);
      } catch (localError) {
        console.error(`[FormContext] Both sessionStorage and localStorage quotas exceeded for ${key}.`, localError);
      }
    }
  }, []);

useEffect(() => {
  setFormDetails(prev => ({
    ...prev,                
    title      : formTitle,
    description: formDescription,
  }));
}, [formTitle, formDescription]);


  // Fetch form details from Firestore or fall back to local storage/session storage
  const fetchFormDetails = useCallback(async (formId) => {
    try {
      if (!formId) {
        console.error('[FormContext] fetchFormDetails: No formId provided.');
        return null;
      }

      // Check if form data exists in local or session storage first
const titleKey = makeKey('title');
const descriptionKey = makeKey('description');
const isPublicKey    = makeKey('isPublic');

const rawTitleSession = sessionStorage.getItem(titleKey);
const rawTitleLocal = localStorage.getItem(titleKey);
//console.log(`[FormContext] Raw sessionStorage[${titleKey}]:`, rawTitleSession);
//console.log(`[FormContext] Raw localStorage[${titleKey}]:`, rawTitleLocal);

const rawDescSession = sessionStorage.getItem(descriptionKey);
const rawDescLocal = localStorage.getItem(descriptionKey);
//console.log(`[FormContext] Raw sessionStorage[${descriptionKey}]:`, rawDescSession);
//console.log(`[FormContext] Raw localStorage[${descriptionKey}]:`, rawDescLocal);

const storedTitle = getFromStorage(titleKey);
const storedDescription = getFromStorage(descriptionKey);
const storedIsPublic    = getFromStorage(isPublicKey);

//console.log(`[FormContext] Parsed storedTitle:`, storedTitle);
//console.log(`[FormContext] Parsed storedDescription:`, storedDescription);


      if ((storedTitle || storedDescription) && storedIsPublic !== null) {
        const fallbackTitle = storedTitle || `Untitled Form ${formId}`;
        const fallbackDescription = storedDescription || '';

        const fallbackIsPublic = storedIsPublic === true || storedIsPublic === 'true';

        setFormTitle(fallbackTitle);
        setFormDescription(fallbackDescription);
        setFormDetails({
          title      : fallbackTitle,
          description: fallbackDescription,
          isPublic   : fallbackIsPublic,
          orgAccess  : 'none',   // prevents “org‑wide” from a previous form
           owner      : null,     // forces ShareForm to recalc once Firestore loads
           sharedWith : {},       // wipe collaborator list
        });

        // keep Navbar badge in sync
        setIsPublished(fallbackIsPublic);
      }

      // If no locally stored data exists, fetch from Firestore
      const { status, form } = await getFormById(formId);

      if (status === 'success' && form) {
//        const { title: name, description, ...rest } = form;
        const { title: name,
                description,
                isPublic,
                ...rest } = form;

        const updatedTitle = name ? name : `Untitled Form ${formId}`;
        const updatedDescription = description || '';

        setFormTitle(updatedTitle);
        setFormDescription(updatedDescription);
        setFormDetails({
          ...rest,
          isPublic,            
          title      : updatedTitle,
          description: updatedDescription,
        });

        saveToStorage(makeKey('title'), updatedTitle);
        saveToStorage(makeKey('description'), updatedDescription);
        saveToStorage(isPublicKey, isPublic);

        // --- NEW: Check published status from Firestore doc
        if (form.status === 'published') {
          console.log(`[FormContext] Detected published form in Firestore with ID ${formId}. Setting isPublished to true.`);
          setIsPublished(true);
        } else {
          console.log(`[FormContext] Detected form with status: ${form.status} (not published). Setting isPublished to false.`);
          setIsPublished(false);
        }
        // --- END NEW

        return form;
      } else {
        console.warn(`[FormContext] Form not found in Firestore for ID ${formId}. Checking local/session storage.`);
        const storedTitle = getFromStorage(makeKey('title'));
        const storedDescription = getFromStorage(makeKey('description'));

        if (storedTitle || storedDescription) {
          const fallbackTitle = storedTitle || `Untitled Form ${formId}`;
          const fallbackDescription = storedDescription || '';

          setFormTitle(fallbackTitle);
          setFormDescription(fallbackDescription);
        setFormDetails({
          title      : fallbackTitle,
          description: fallbackDescription,
          isPublic   : false,
        });

          // Also set isPublished to false, because we didn't find a published doc
          console.log(`[FormContext] No Firestore doc found. Setting isPublished = false (found local data).`);
          setIsPublished(false);
         saveToStorage(isPublicKey, false);

          return { title: fallbackTitle, description: fallbackDescription };
        }

        console.warn(`[FormContext] No form data found in storage or Firestore. Creating new default form details for form ID: ${formId}`);
        const newFormDetails = {
          title      : `Untitled Form ${formId}`,
          description: 'Add a description',
          isPublic   : false, 
        };


        setFormTitle(newFormDetails.title);
        setFormDescription(newFormDetails.description);
        setFormDetails(newFormDetails);

        saveToStorage(makeKey('title'), newFormDetails.title);
        saveToStorage(makeKey('description'), newFormDetails.description);
        saveToStorage(isPublicKey, false);

        // IMPORTANT: If the doc doesn’t even exist, it can’t be published
        console.log(`[FormContext] Setting isPublished = false (no doc found at all).`);
        setIsPublished(false);

        return newFormDetails;
      }
    } catch (error) {
      console.error(`[FormContext] fetchFormDetails: Error fetching form details for form ID ${formId}:`, error);
      return null;
    }
  }, [getFromStorage, saveToStorage, makeKey]);

// ────────────────────────────────────────────────────────────────
// Draft overlay actions
// ────────────────────────────────────────────────────────────────
const discardDraftOverlay = useCallback(async (targetFormId) => {
  try {
    const id = targetFormId || formId;

    if (!id) {
      console.error('[FormContext] discardDraftOverlay: No formId provided.');
      return { status: 'error', message: 'No formId provided.' };
    }

    console.log('[FormContext] Discarding draft overlay for formId:', id);

    const formRef = doc(db, 'forms', id);

    // Remove BOTH the nested payload + the root-level overlay index fields
    await updateDoc(formRef, {
      draftOverlay: deleteField(),
      draftOverlayStatus: deleteField(),
      draftOverlaySavedAt: deleteField(),
      draftOverlaySavedBy: deleteField(),
      // Do NOT touch root `status`, or root blobs — published remains live.
      lastModified: serverTimestamp(),
    });

    console.log('[FormContext] Draft overlay discarded successfully for formId:', id);

    // Update local state optimistically so Navbar updates instantly
    setFormDetails((prev) => {
      const next = { ...(prev || {}) };
      delete next.draftOverlay;
      delete next.draftOverlayStatus;
      delete next.draftOverlaySavedAt;
      delete next.draftOverlaySavedBy;
      return next;
    });

    // Re-fetch to ensure everything is consistent with Firestore
    await fetchFormDetails(id);

    return { status: 'success', message: 'Draft overlay discarded.' };
  } catch (error) {
    console.error('[FormContext] discardDraftOverlay: Error discarding draft overlay:', error);
    return { status: 'error', message: error?.message || 'Failed to discard draft overlay.', error };
  }
}, [formId, fetchFormDetails]);


  // UseEffect to update localStorage when formId changes and we are in the form-builder path
  useEffect(() => {
    const currentStoredId = localStorage.getItem('formId');
    if (formId && formId !== currentStoredId && location.pathname.startsWith('/form-builder')) {
      localStorage.setItem('formId', formId);
      // console.log(`[FormContext] Saved formId ${formId} to localStorage after change.`);
    }
  }, [formId, location.pathname]);

  // Reset settings or fetch form details when formId changes
  useEffect(() => {
  if (!formId) {
    setFormIdReady(false); // Reset readiness if formId becomes null
    return;
  }

    if (previousFormId.current !== formId) {
        /* ────────────────────────────────────────────────
       NEW:  hard‑reset formDetails so old permissions
       can’t leak into the next form while we’re still
       waiting for fetchFormDetails() to run.
    ──────────────────────────────────────────────── */
    setFormDetails({});          // clears owner / sharedWith / orgAccess


      // console.log('[FormContext] Form ID changed, fetching form details or resetting to defaults.');
      fetchFormDetails(formId).then((fetchedDetails) => {
        if (fetchedDetails) {
//          console.log('[FormContext] Form details fetched successfully:', fetchedDetails);
        } else {
          console.warn('[FormContext] No form details found, defaults may be used.');
        }
      })
      .catch((error) => {
        console.error('[FormContext] Error fetching form details:', error);
      })
      .finally(() => {
        setFormIdReady(true); // Ensure readiness is set regardless of success or error
      });

    previousFormId.current = formId;
  } else {
    setFormIdReady(true); // For already loaded formId
  }
}, [formId, fetchFormDetails]);


  // Save form title and description with throttling (debounce)
  useEffect(() => {
    if (!formId) return;

    const saveSettings = () => {
      saveToStorage(makeKey('title'), formTitle);
      saveToStorage(makeKey('description'), formDescription);
    };

    const timeoutId = setTimeout(saveSettings, 300);
    return () => clearTimeout(timeoutId);
  }, [formId, formTitle, formDescription, makeKey, saveToStorage]);

const createNewFormInFirestore = async (userId) => {
//   console.log('[FormContext: createNewFormInFirestore] createNewFormInFirestore triggered');

  if (isFormBeingCreated) {
//       console.log('[FormContext: createNewFormInFirestore] Form creation is already in progress.');
    return null;
  }

  try {
    setIsFormBeingCreated(true);
//      console.log('[FormContext: createNewFormInFirestore] Checking user permissions for creating forms.');

    const hasPermission = await checkUserPermission(userId, 'createForms');
    if (!hasPermission) {
      console.error('[FormContext: createNewFormInFirestore] Permission denied to create new forms.');
      alert('You do not have permission to create new forms.');
      setIsFormBeingCreated(false);
      return null;
    }

//      console.log('[FormContext: createNewFormInFirestore] User has permission. Proceeding to create new form.');

    // Clear the old formId before creating a new form
    localStorage.removeItem('formId');
    sessionStorage.removeItem(`form_${formId}_layoutConfig`);
    safeRemoveItem(`form_${formId}_fieldsMetadata`);
    setFormId(null);

    // Generate a new formId using uuidv4
    const newFormId = uuidv4();
//      console.log(`[FormContext: createNewFormInFirestore] Generated new formId: ${newFormId}`);

    setFormId(newFormId);
    localStorage.setItem('formId', newFormId);

    // ***** NEW CODE ADDED BELOW *****
    // Signal that this is a new form by setting a flag in localStorage.
    // FieldMetadataProvider will check for this flag (using key `form_${newFormId}_isNew`)
    // and clear any stale fields metadata if present.
    localStorage.setItem(`form_${newFormId}_isNew`, 'true');
    // ***** END NEW CODE *****

    // Initialize default form details for the new form
    const defaultTitle = `Untitled Form ${newFormId}`;
    const defaultDescription = '';
    setFormTitle(defaultTitle);
    setFormDescription(defaultDescription);
    setFormDetails({
      owner      : userId,
      title      : defaultTitle,
      description: 'Add a new description',
      isPublic   : false,          // 🔸 NEW
    });

//      console.log(`[FormContext: createNewFormInFirestore] Saving new form ${newFormId} to Firestore.`);

    // Reset additional form states
    setIsPublished(false);
    setIsFormValid(false);
//      console.log(`[FormContext: createNewFormInFirestore] New form created with ID: ${newFormId}.`);

    // Write the new form document to Firestore
    const formRef = doc(db, 'forms', newFormId);
    await setDoc(formRef, {
      owner: userId,
      title: defaultTitle,
      description: defaultDescription,
      createdAt: serverTimestamp(),
      lastModified: serverTimestamp(),
      status: 'draft'
    });
 // the Firestore doc now exists → it’s no longer a pure local draft
 sessionStorage.removeItem(`form_${newFormId}_isNew`);
 localStorage.removeItem(`form_${newFormId}_isNew`);

    setIsFormBeingCreated(false);
    return newFormId;
  } catch (error) {
    console.error('[FormContext: createNewFormInFirestore] Error creating new form:', error);
    alert(`Failed to create new form due to an error: ${error.message}`);
    setIsFormBeingCreated(false);
    return null;
  }
};

const initializeNewFormLocally = (userId) => {
//  console.log('[FormContext: initializeNewFormLocally] Triggered local-only form initialization');

  // Generate a new formId without writing to Firestore
  const newFormId = uuidv4();
  const defaultTitle = `Untitled Form ${newFormId}`;
  const defaultDescription = '';

  // Save formId locally
  setFormId(newFormId);
  localStorage.setItem('formId', newFormId);

  // Signal this is a brand new form session
  localStorage.setItem(`form_${newFormId}_isNew`, 'true');

  // Set initial form metadata
  setFormTitle(defaultTitle);
  setFormDescription(defaultDescription);
  setFormDetails({
    owner: userId,
    title: defaultTitle,
    description: defaultDescription,
    isPublic   : false,
  });

  // Set form creation state flags
  setIsPublished(false);
  setIsFormValid(false);
  setIsFormModified(false);

  // Optionally log confirmation
//  console.log(`[FormContext: initializeNewFormLocally] Local form initialized with ID: ${newFormId}`);
  
  return newFormId;
};

  // Function to publish the form
  const publishForm = async () => {
    if (!isFormValid) {
      console.error(`[FormContext] Cannot publish form ${formId} because it is not valid.`);
      alert('The form cannot be published because it is not valid.');
      return;
    }
    setIsPublished(true);
  };

  const updateTitle = useCallback((newTitle) => {
    setFormTitle((prevTitle) => {
      if (prevTitle !== newTitle) {
        setIsFormModified(true);
        setFormDetails((prevDetails) => ({
          ...prevDetails,
          title: newTitle,
        }));
        return newTitle;
      }
      return prevTitle;
    });
  }, []);

  const updateDescription = useCallback((newDescription) => {
    setFormDescription((prevDescription) => {
      if (prevDescription !== newDescription) {
        setIsFormModified(true);
        setFormDetails((prevDetails) => ({
          ...prevDetails,
          description: newDescription,
        }));
        return newDescription;
      }
      return prevDescription;
    });
  }, []);

  // Function to save the form title and description to Firestore
  const saveFormDetails = async () => {
    if (!formTitle || !isFormModified) {
      return;
    }
    try {
      const formData = {
        name: formTitle,
        description: formDescription || '',
      };

      setFormDetails(formData);
      setIsFormModified(false);
    } catch (error) {
      console.error(`[FormContext] Error saving form details for form ${formId}:`, error);
    }
  };

  return (
    <FormContext.Provider value={{
      formId: memoizedFormId,
      formIdReady,
      setFormId,
      formDetails,
      setFormDetails,
      formTitle: memoizedFormTitle,
      setFormTitle: updateTitle,
      formDescription: memoizedFormDescription,
      setFormDescription: updateDescription,
      updateTitle,
      updateDescription,
      saveFormDetails,
      isPublished,
      setIsPublished,
      isFormValid,
      setIsFormValid,
      isFormModified,
      setIsFormModified,
      createNewFormInFirestore,
      initializeNewFormLocally,
      publishForm,
      fetchFormDetails,
      discardDraftOverlay,
    }}>
      {children}
    </FormContext.Provider>
  );
};

export const useFormContext = () => useContext(FormContext);

export default FormProvider;
